import React, { useState } from 'react'
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import { generateDisplayFields } from "utils/fieldMapper";
import DataTable from 'Common/DataTable/DataTable';
import feilds from "../../json/RoomBookingFields.json"
import { GetAllBookings } from 'adminApi/RoomBookingApi/RoomBooking';
import { useRoleVerify } from 'Common/RoleVerify';
import useRoomTableConfig from './useRoomTableConfig';
import FormSelect from 'Common/Custom/FormSelect';
import FormDatePicker from 'Common/Custom/ReactFlatpickr';

const BookingFilters = ({ filters, setFilters }) => {
  const statusOptions = [
    { value: "", label: "All" },
    { value: "0", label: "Pending" },
    { value: "2", label: "booked" },
    { value: "4", label: "checkin" },
    { value: "5", label: "checkout" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "from_date" && updated.to_date) {
        if (new Date(updated.to_date) < new Date(value)) updated.to_date = value;
      }
      if (name === "to_date" && updated.from_date) {
        if (new Date(value) < new Date(updated.from_date)) updated.to_date = updated.from_date;
      }
      return updated;
    });
  };

  return (
    <div className="row g-3 mb-3">
      <div className="col-md-3">
        <FormSelect
          field={{ name: "booking_status_code", label: "Booking Status" }}
          value={filters.booking_status_code || ""}
          handleChange={handleChange}
          options={statusOptions}
        />
      </div>
      <div className="col-md-3">
        <FormDatePicker
          field={{ name: "from_date", label: "From Date", placeholder: "From Date" }}
          value={filters.from_date || ""}
          handleChange={handleChange}
        />
      </div>
      <div className="col-md-3">
        <FormDatePicker
          field={{
            name: "to_date",
            label: "To Date",
            placeholder: "To Date",
            minDate: filters.from_date || null,
          }}
          value={filters.to_date || ""}
          handleChange={handleChange}
        />
      </div>
    </div>
  );
};

const BookingTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});

  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = feilds;

  // All filter params flow into the fetch hook and reach the API
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAllBookings, {
    page,
    limit,
    search: debouncedSearch,
    ...appliedFilters,          // ← booking_status_code, from_date, to_date go here
  });

  const displayFields = generateDisplayFields(tabledata);

  // ✅ No client-side FilterStatus — backend handles filtering now
  const { formattedData, columns } = useRoomTableConfig({
    data: data || [],
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: false,
    isView: true,
    IsChackIn: true,
    IsEditDisble: true,
    IsEditDisbleKey: 'edit_disable',
    config: {
      entityName: "Room Booking",
      editUrl: "update-booking",
      idKey: "booked_id",
      deleteApi: '',
      displayFields,
      ViewUrl: 'view-booking',
      ChackInUrl: 'create-checkin',
      IsCheckInDisableKey: 'booking_status_code',
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const handleApply = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleReset = () => {
    setFilters({});
    setAppliedFilters({});
    setSearch("");
    setPage(1);
  };

  return (
    <>
      <BookingFilters filters={filters} setFilters={setFilters} />

      <div className="mb-4 ms-sm-auto d-flex align-items-center justify-content-end gap-2">
        <button type="button" className="btn btn-primary me-2" onClick={handleApply}>
          Apply Filters
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      <DataTable
        columns={columns}
        data={formattedData}
        options={{
          loading,
          error,
          sortableColumns: displayFields.map((f) => f.accessor),
        }}
        Search={{
          search: true,
          SearchInputValue: search,
          handleSearch,
        }}
        Entries={{
          Entries: true,
          limit,
          onLimitChange: (newLimit) => { setLimit(newLimit); setPage(1); },
          options: sizeOptions,
        }}
        pagination={{
          isActive: true,
          meta,
          onPageChange: (p) => setPage(p),
        }}
      />
    </>
  );
};

export default BookingTable;